import { AlertCircle, FileText, RefreshCw, UploadCloud, X } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { forwardRef } from "react";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

import { FileTypeConfig } from "./types";
import { formatBytes, formatFileType } from "./format";
import { FileUploadViewProps } from "./view-props";

/** Transição padrão (spring suave) reutilizada nas micro-interações. */
const SPRING = { type: "spring", stiffness: 380, damping: 30 } as const;

interface DropzonePromptProps {
  palette: FileTypeConfig;
  isDragging: boolean;
  validationError: string;
  maxFileSizeMB: number;
  reduceMotion: boolean;
}

/** Conteúdo do dropzone quando ainda não há arquivo (ou há erro de validação). */
function DropzonePrompt({
  palette,
  isDragging,
  validationError,
  maxFileSizeMB,
  reduceMotion,
}: DropzonePromptProps) {
  return (
    <div className="pointer-events-none flex flex-col items-center px-2 text-center">
      <motion.div
        animate={{ scale: isDragging ? 1.1 : 1 }}
        transition={SPRING}
        className={cn(
          "mb-3 grid size-12 place-items-center rounded-xl border bg-card shadow-sm sm:size-14",
          "transition-shadow duration-300 group-hover:shadow-md",
          isDragging ? palette.activeBorder : "border-border",
          validationError && "border-red-200 bg-red-50 dark:border-red-800/50 dark:bg-red-900/20",
        )}
      >
        {validationError ? (
          <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400 sm:h-6 sm:w-6" />
        ) : (
          <motion.div
            animate={reduceMotion ? undefined : { y: isDragging ? [0, -5, 0] : 0 }}
            transition={
              isDragging ? { repeat: Infinity, duration: 0.9, ease: "easeInOut" } : SPRING
            }
          >
            <UploadCloud className={cn("h-6 w-6 sm:h-7 sm:w-7", palette.icon)} />
          </motion.div>
        )}
      </motion.div>

      <div className="space-y-1">
        {validationError ? (
          <p className="break-words px-2 text-xs font-semibold text-red-600 dark:text-red-400 sm:text-sm">
            {validationError}
          </p>
        ) : (
          <>
            <p className={cn("px-2 text-xs font-medium sm:text-sm", palette.text)}>
              <span className="font-semibold underline underline-offset-2 sm:no-underline group-hover:sm:underline">
                {isDragging ? "Solte o arquivo aqui" : "Clique para selecionar"}
              </span>
              {!isDragging && (
                <span className="hidden text-muted-foreground sm:inline"> ou arraste e solte</span>
              )}
            </p>
            <p className="hidden text-[10px] text-muted-foreground sm:block sm:text-xs">
              Também aceita colar (Ctrl+V) um print
            </p>
            <p className="text-[10px] text-muted-foreground sm:text-xs">
              Tamanho máximo {maxFileSizeMB}MB
            </p>
          </>
        )}
      </div>
    </div>
  );
}

interface DropzonePreviewProps {
  palette: FileTypeConfig;
  file: File;
  onSwap: () => void;
  onRemove: (e: React.MouseEvent) => void;
}

/** Conteúdo do dropzone com o arquivo selecionado e as ações de trocar/remover. */
function DropzonePreview({ palette, file, onSwap, onRemove }: DropzonePreviewProps) {
  return (
    <div className="flex w-full flex-col items-center">
      <motion.div
        initial={{ scale: 0.6, rotate: -8 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={SPRING}
        className={cn(
          "mb-3 grid size-12 place-items-center rounded-xl border bg-card shadow-sm sm:size-14",
          palette.border,
        )}
      >
        <FileText className={cn("h-6 w-6 sm:h-7 sm:w-7", palette.fileIcon)} />
      </motion.div>

      <div className="mb-3 w-full overflow-hidden px-2 text-center sm:mb-4">
        <p className={cn("line-clamp-2 break-all text-xs font-semibold sm:text-sm", palette.text)}>
          {file.name}
        </p>
        <p className="mt-1 text-[10px] text-muted-foreground sm:text-xs">
          {formatFileType(file)} • {formatBytes(file.size)}
        </p>
      </div>

      <div className="flex w-full flex-col gap-2 px-4 sm:w-auto sm:flex-row sm:px-0">
        <motion.div className="w-full sm:w-auto" whileHover={{ y: -1 }} whileTap={{ scale: 0.96 }}>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className={cn("h-8 w-full bg-card/80 text-xs shadow-sm hover:bg-card", palette.button)}
            onClick={(e) => {
              e.stopPropagation();
              onSwap();
            }}
          >
            <RefreshCw className="mr-2 h-3 w-3" />
            Trocar
          </Button>
        </motion.div>
        <motion.div className="w-full sm:w-auto" whileHover={{ y: -1 }} whileTap={{ scale: 0.96 }}>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-8 w-full text-xs text-red-500 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-900/20 dark:hover:text-red-300"
            onClick={onRemove}
          >
            <X className="mr-2 h-3 w-3" />
            Remover
          </Button>
        </motion.div>
      </div>
    </div>
  );
}

/** Variante padrão: área de arrastar-e-soltar com label opcional. */
export const FileUploadDropzone = forwardRef<HTMLDivElement, FileUploadViewProps>(
  function FileUploadDropzone({ state, label, required, disabled, maxFileSizeMB, className }, ref) {
    const {
      palette,
      selectedFile,
      isDragging,
      validationError,
      inputProps,
      dragHandlers,
      hoverHandlers,
      triggerFileInput,
      removeFile,
    } = state;
    const reduceMotion = useReducedMotion() ?? false;

    return (
      <div ref={ref} className={cn("w-full space-y-2", className)}>
        {label && (
          <div className="flex items-end justify-between">
            <Label className={cn("text-foreground", disabled && "opacity-50")}>
              {label} {required && <span className="ml-1 text-destructive">*</span>}
            </Label>
            {selectedFile && (
              <span className="text-[10px] text-muted-foreground sm:text-xs">
                {formatBytes(selectedFile.size)}
              </span>
            )}
          </div>
        )}

        <motion.div
          {...dragHandlers}
          {...hoverHandlers}
          onClick={triggerFileInput}
          animate={{ scale: isDragging ? 1.01 : 1 }}
          whileTap={disabled ? undefined : { scale: 0.99 }}
          transition={SPRING}
          className={cn(
            "group relative flex min-h-[150px] w-full cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed p-4 sm:min-h-[170px] sm:p-6",
            "transition-colors duration-300 ease-out",
            isDragging ? palette.activeBorder : palette.border,
            isDragging ? palette.activeBg : palette.bg,
            isDragging && "shadow-lg",
            !disabled && !isDragging && !validationError && "hover:border-solid hover:shadow-md",
            disabled ? "cursor-not-allowed opacity-50" : "",
            validationError && "border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-900/10",
          )}
        >
          <input {...inputProps} />

          <AnimatePresence mode="wait" initial={false}>
            {selectedFile ? (
              <motion.div
                key="preview"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
                className="flex w-full flex-col items-center"
              >
                <DropzonePreview
                  palette={palette}
                  file={selectedFile}
                  onSwap={triggerFileInput}
                  onRemove={removeFile}
                />
              </motion.div>
            ) : (
              <motion.div
                key="prompt"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
                className="flex w-full flex-col items-center"
              >
                <DropzonePrompt
                  palette={palette}
                  isDragging={isDragging}
                  validationError={validationError}
                  maxFileSizeMB={maxFileSizeMB}
                  reduceMotion={reduceMotion}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    );
  },
);
